import {BenchlingExpression} from 'benchling-expressions/BenchlingExpressionTypes';
import {
  ProcessSchemaMapperType,
  ProcessFlowchartNodeDetailsType,
  ProcessExecutionType,
  ProcessMapperTokenType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import * as net from 'util/Net';

import {ProcessFlowchartConfigForEditorWithLatestVersionGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessFlowchartConfigForEditorWithLatestVersionGraphQL';

export function processFlowchartConfigURL(): string {
  return '/1/api/process-flowchart-config';
}

// Base Types
interface ProcessFlowchartNodeConfigSpecBase<TNodeDetails> {
  clientId: string;
  nodeDetails: TNodeDetails;
}

export interface ProcessFlowchartEdgeConfigSpec {
  clientId: string;
  fromNodeClientId: string;
  toNodeClientId: string;
}

// Task Definition Types

/**
 * Temporary interface to represent a task schema on a TaskDefinitionVersionSpec.
 * This field is not currently supported by the API, but is useful on the frontend for
 * keeping the data together, and we may enable updating a task schema via the flowchart config API in the future.
 */
export interface TaskSchemaSpec {
  id: string;
  name: string;
  executionType: ProcessExecutionType;
  fields: Array<SchemaField>;
  outputSchema: {
    id: string;
    name: string;
    fields: Array<SchemaField>;
  } | null;
}

export interface NewTaskDefinitionVersionSpec<TApplicationData = unknown> {
  taskSchemaId: string;
  applicationData: TApplicationData | null;
}

export interface TaskDefinitionVersionSpec<TApplicationData = unknown> {
  clientId: string;
  currentVersionId: string | null;
  newProcessTaskDefinitionVersion: NewTaskDefinitionVersionSpec<TApplicationData>;
}

export interface RootTaskDefinitionSpec<
  TApplicationData = unknown,
> extends TaskDefinitionVersionSpec<TApplicationData> {}

// Router Function Types
export interface BaseRouterFunctionSpec {
  edgeConfigClientId: string | null;
  name: string;
}

export interface RouterFunctionSpec {
  name: string;
  edgeConfigClientId: string | null;
  expressionJson: BenchlingExpression | null;
}

// Node Types
export interface TaskNodeDetailsSpec {
  type: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS;
  taskSchemaId: string;
  taskDefinitionVersionClientId: string | null;
  extension: Record<string, unknown> | null;
}

export interface RouterNodeDetailsSpec {
  type: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS;
  name: string;
  routerFunctions: Array<RouterFunctionSpec>;
}

export interface RootNodeDetailsSpec {
  type: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS;
  taskSchemaId: string;
  taskDefinitionVersionClientId: string | null;
}

export interface OutputNodeDetailsSpec {
  type: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
  outputSchemaId: string;
}

export interface EndNodeDetailsSpec {
  type: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS;
  name: string;
}

export type NodeDetailsSpec =
  | TaskNodeDetailsSpec
  | RouterNodeDetailsSpec
  | RootNodeDetailsSpec
  | OutputNodeDetailsSpec
  | EndNodeDetailsSpec;

type MappableNodeDetailsSpec = TaskNodeDetailsSpec | OutputNodeDetailsSpec;
export type MappableNodeConfigSpec = ProcessFlowchartNodeConfigSpecBase<MappableNodeDetailsSpec> & {
  inboundSchemaMappers: Array<ProcessFlowchartSchemaMapperSpec>;
};

export type ProcessFlowchartNodeConfigSpec =
  | MappableNodeConfigSpec
  | ProcessFlowchartNodeConfigSpecBase<NodeDetailsSpec>;

// Schema Mapper Types
export interface ProcessFlowchartSchemaFieldMapperConfigSpec {
  fromSchemaId?: string | null;
  fromProcessTaskDefinitionVersionClientId: string | null;
  mapsFromType: ProcessMapperTokenType | null;
  toProcessFieldClientIdentifier: string | null;
  lookupConfig: LookupConfigSpec;
  toSchemaFieldId?: string | null;
  resultMustBeNonNullToMap: boolean | null;
}

export interface ProcessFlowchartSchemaMapperSourceConfigSpec {
  fromProcessTaskDefinitionVersionClientId: string;
  mapsFromType: ProcessMapperTokenType;
}

export interface ProcessFlowchartSchemaMapperConfigSpec {
  clientId: string;
  toSchemaId?: string | null;
  toProcessTaskDefinitionVersionClientId: string | null;
  mapsToType: ProcessMapperTokenType | null;
  schemaFieldMapperConfigs: Array<ProcessFlowchartSchemaFieldMapperConfigSpec>;
  mapperType: ProcessSchemaMapperType;
  schemaMapperSourceConfigs?: Array<ProcessFlowchartSchemaMapperSourceConfigSpec>;
}

export interface ProcessFlowchartSchemaMapperDependencySpec {
  fromNodeConfigClientId: string;
}

export interface ProcessFlowchartSchemaMapperSpec {
  toNodeConfigClientId: string;
  schemaMapperConfigClientId: string | null;
  schemaMapperDependencies: Array<ProcessFlowchartSchemaMapperDependencySpec>;
}

// Flowchart Config Types
export interface ProcessFlowchartSpec {
  clientId: string;
  nodes: Array<ProcessFlowchartNodeConfigSpec>;
  edges: Array<ProcessFlowchartEdgeConfigSpec>;
}

export interface ProcessFlowchartConfigSpec {
  rootProcessTaskDefinitionVersion: RootTaskDefinitionSpec | null;
  allProcessTaskDefinitionVersions: Array<TaskDefinitionVersionSpec> | null;
  availableTaskSchemaIds: Array<string>;
  schemaMapperConfigs: Array<ProcessFlowchartSchemaMapperConfigSpec>;
  templateFlowchart: ProcessFlowchartSpec | null;
  isFlowchartModifiableOnCreation: boolean;
  isFlowchartModifiableAfterCreation: boolean;
}

// API Request/Response Types
interface CreateProcessFlowchartConfigRequest {
  processTaskSchemaId: string;
  flowchartConfigSpec: ProcessFlowchartConfigSpec;
}

interface UpdateProcessFlowchartConfigRequest {
  flowchartConfigId: string;
  flowchartConfigSpec: ProcessFlowchartConfigSpec;
  currentVersionId: string | null;
}

interface CreateProcessFlowchartConfigResponse {
  flowchartConfig: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL;
  cidToIdMap: CidIdMap;
}

interface UpdateProcessFlowchartConfigResponse {
  flowchartConfig: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL;
  cidToIdMap: CidIdMap;
}

// API Functions
export function createProcessFlowchartConfig(
  data: CreateProcessFlowchartConfigRequest
): Promise<CreateProcessFlowchartConfigResponse> {
  return net.postJSONPromise({
    url: processFlowchartConfigURL(),
    data,
  });
}

export function updateProcessFlowchartConfig(
  data: UpdateProcessFlowchartConfigRequest
): Promise<UpdateProcessFlowchartConfigResponse> {
  return net.patchJSONPromise({
    url: processFlowchartConfigURL(),
    data,
  });
}
